import { IContent } from '@src/interfaces/community';
import { devices } from '@src/styles/theme';
import { avatarInitial, avatarTint } from '@src/utils/chatAvatar';
import {
  formatBloodProduct,
  formatBloodType,
  formatDeadline,
  formatDeadlineShort,
  formatRelativeTime,
} from '@src/utils/donationFormat';
import { useRouter } from 'next/router';
import React from 'react';
import styled from 'styled-components';

/**
 * 헌혈요청 목록 카드 (Pple Blood Request List 디자인 이식).
 *
 * 구성: [좌] 혈액형 박스 · [중앙] 상태태그/마감/시간·조회 → 제목 → 장소 → 작성자+모집진행바 · [우] 대화하기/상세보기
 * 긴급(마감 3일 이내)이면 카드 테두리·혈액박스·태그가 강조색으로 바뀐다.
 * 백엔드에 값이 없는 항목(마감·지역·병원·필요수량)은 조용히 숨긴다 → 기존 글도 깨지지 않는다.
 */
type Props = {
  community: IContent & {
    region?: string | null;
    deadlineAt?: string | null;
    hospitalName?: string | null;
    requiredCount?: number | null;
    fulfilledCount?: number | null;
    viewsCount?: number;
    status?: string;
  };
  /** 카드에서 바로 대화 시작 */
  onChat?: (community: any) => void;
  chatDisabled?: boolean;
};

const DonationCard = ({ community, onChat, chatDisabled }: Props) => {
  const router = useRouter();

  const detailHref = `/community/donation/detail/${community.uuid}`;
  const bloodType = formatBloodType(community?.patient?.bloodType);
  const bloodProduct = formatBloodProduct(community?.bloodProduct);
  const deadline = formatDeadline(community?.deadlineAt);
  const urgent = !!deadline?.urgent;
  const writerName = community?.writer?.displayName ?? '';
  const tint = avatarTint(community?.writer?.uuid || community.uuid);
  const views = community?.viewsCount ?? community?.hits ?? 0;

  // 장소: "지역 · 지정헌혈 · 병원" (있는 것만 이어붙인다)
  const place = [community?.region, '지정헌혈', community?.hospitalName]
    .filter(Boolean)
    .join(' · ');

  const required = community?.requiredCount ?? null;
  const fulfilled = community?.fulfilledCount ?? 0;
  const complete = required != null && fulfilled >= required;
  const pct =
    required && required > 0
      ? Math.min(100, Math.round((fulfilled / required) * 100))
      : 0;
  const donationCount = community?.writer?.bloodDonationCount;

  return (
    <Card urgent={urgent} onClick={() => router.push(detailHref)}>
      <BloodBox urgent={urgent}>
        <BloodType urgent={urgent}>{bloodType || '혈액형'}</BloodType>
        <BloodKind>{bloodProduct}</BloodKind>
      </BloodBox>

      <Body>
        <TopRow>
          <Tag urgent={urgent}>{urgent ? '긴급' : '모집 중'}</Tag>
          {urgent && community?.deadlineAt && (
            <Deadline>{formatDeadlineShort(community.deadlineAt)}</Deadline>
          )}
          <Meta>
            {formatRelativeTime(community?.createdAt)} · 조회 {views}
          </Meta>
        </TopRow>

        <Title>{community?.title}</Title>
        {place && <Place>{place}</Place>}

        <BottomRow>
          <Avatar tint={tint.tint} ink={tint.ink}>
            {avatarInitial(writerName)}
          </Avatar>
          <Author>{writerName}</Author>
          {donationCount != null && donationCount > 0 && (
            <AuthorBadge>헌혈 {donationCount}회</AuthorBadge>
          )}
          {required != null ? (
            <Progress>
              <Track>
                <Fill complete={complete} style={{ width: `${pct}%` }} />
              </Track>
              <ProgressLabel complete={complete}>
                {fulfilled}/{required}
              </ProgressLabel>
            </Progress>
          ) : (
            <Metrics>
              <span>응원 {community?.likesCount ?? 0}</span>
              <span>댓글 {community?.repliesCount ?? 0}</span>
            </Metrics>
          )}
        </BottomRow>
      </Body>

      <Actions onClick={(e) => e.stopPropagation()}>
        {onChat &&
          (complete ? (
            <DoneButton disabled>모집 완료</DoneButton>
          ) : (
            <ChatButton
              onClick={() => onChat(community)}
              disabled={chatDisabled}
            >
              {chatDisabled ? '여는 중…' : '대화하기'}
            </ChatButton>
          ))}
        <DetailButton onClick={() => router.push(detailHref)}>
          상세보기
        </DetailButton>
      </Actions>
    </Card>
  );
};

const Card = styled.div<{ urgent: boolean }>`
  display: flex;
  align-items: flex-start;
  gap: 20px;
  background: #ffffff;
  border: 1px solid ${({ urgent }) => (urgent ? '#FFD3D3' : '#EDEDED')};
  border-radius: 20px;
  padding: 22px 24px;
  margin-bottom: 12px;
  cursor: pointer;
  transition: border-color 0.15s;
  &:hover {
    border-color: #ffd3d3;
  }
  @media ${devices.tablet} {
    gap: 13px;
    padding: 16px;
    border-radius: 16px;
  }
`;
const BloodBox = styled.div<{ urgent: boolean }>`
  width: 76px;
  height: 76px;
  flex: none;
  border-radius: 16px;
  background: ${({ urgent }) => (urgent ? '#FFF1ED' : '#F9F9F9')};
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 2px;
  @media ${devices.tablet} {
    width: 56px;
    height: 56px;
    border-radius: 13px;
  }
`;
const BloodType = styled.span<{ urgent: boolean }>`
  font-family: 'Var', sans-serif;
  font-size: 20px;
  font-weight: 700;
  color: ${({ urgent }) => (urgent ? '#FF4651' : '#222222')};
  @media ${devices.tablet} {
    font-size: 15px;
  }
`;
const BloodKind = styled.span`
  font-size: 11.5px;
  font-weight: 600;
  color: #767676;
  @media ${devices.tablet} {
    font-size: 10.5px;
  }
`;
const Body = styled.div`
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 9px;
`;
const TopRow = styled.div`
  display: flex;
  align-items: center;
  gap: 7px;
  flex-wrap: wrap;
`;
const Badge = styled.span`
  height: 22px;
  padding: 0 9px;
  border-radius: 11px;
  font-size: 11px;
  font-weight: 700;
  display: inline-flex;
  align-items: center;
  line-height: 1;
  white-space: nowrap;
  @media ${devices.tablet} {
    height: 20px;
    padding: 0 8px;
    border-radius: 10px;
    font-size: 10.5px;
  }
`;
const Tag = styled(Badge)<{ urgent: boolean }>`
  background: ${({ urgent }) => (urgent ? '#FF4651' : '#F4F4F4')};
  color: ${({ urgent }) => (urgent ? '#ffffff' : '#767676')};
`;
const Deadline = styled(Badge)`
  background: #fff1ed;
  color: #ff4651;
`;
const Meta = styled.span`
  margin-left: auto;
  font-size: 12px;
  color: #b7b7b7;
`;
const Title = styled.h3`
  margin: 0;
  font-family: 'Var', sans-serif;
  font-size: 18px;
  font-weight: 700;
  line-height: 26px;
  color: #222222;
  @media ${devices.tablet} {
    font-size: 14.5px;
    line-height: 21px;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
  }
`;
const Place = styled.span`
  font-size: 13.5px;
  color: #767676;
  @media ${devices.tablet} {
    font-size: 11.5px;
  }
`;
const BottomRow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding-top: 12px;
  border-top: 1px solid #f5f5f5;
`;
const Avatar = styled.div<{ tint: string; ink: string }>`
  width: 28px;
  height: 28px;
  flex: none;
  border-radius: 14px;
  background: ${({ tint }) => tint};
  color: ${({ ink }) => ink};
  font-size: 12px;
  font-weight: 700;
  display: flex;
  align-items: center;
  justify-content: center;
  @media ${devices.tablet} {
    display: none;
  }
`;
const Author = styled.span`
  font-size: 13px;
  font-weight: 600;
  color: #222222;
  @media ${devices.tablet} {
    font-size: 12px;
  }
`;
const AuthorBadge = styled.span`
  font-size: 12px;
  color: #b7b7b7;
  @media ${devices.tablet} {
    display: none;
  }
`;
/** 모집 진행바 — 디자인은 우측 고정폭 210px */
const Progress = styled.div`
  margin-left: auto;
  width: 210px;
  flex: none;
  display: flex;
  align-items: center;
  gap: 8px;
  @media ${devices.tablet} {
    width: auto;
    flex: 1;
    min-width: 0;
  }
`;
const Track = styled.div`
  flex: 1;
  min-width: 0;
  height: 6px;
  border-radius: 3px;
  background: #f4f4f4;
  overflow: hidden;
  @media ${devices.tablet} {
    height: 5px;
  }
`;
const Fill = styled.div<{ complete: boolean }>`
  height: 100%;
  border-radius: 3px;
  background: ${({ complete }) => (complete ? '#2E9B62' : '#FF4651')};
  transition: width 0.3s;
`;
const ProgressLabel = styled.span<{ complete: boolean }>`
  flex: none;
  font-size: 12px;
  font-weight: 700;
  color: ${({ complete }) => (complete ? '#2E9B62' : '#FF4651')};
  @media ${devices.tablet} {
    font-size: 11px;
  }
`;
const Metrics = styled.div`
  margin-left: auto;
  display: flex;
  gap: 12px;
  font-size: 12px;
  color: #b7b7b7;
  @media ${devices.tablet} {
    display: none;
  }
`;
const Actions = styled.div`
  width: 112px;
  flex: none;
  align-self: center;
  display: flex;
  flex-direction: column;
  gap: 8px;
  @media ${devices.tablet} {
    display: none;
  }
`;
const ChatButton = styled.button`
  height: 36px;
  border: none;
  border-radius: 18px;
  background: #ff4651;
  color: #ffffff;
  font-size: 13px;
  font-weight: 700;
  cursor: pointer;
  &:hover:not(:disabled) {
    background: #e03a44;
  }
  &:disabled {
    background: #ffa3a3;
    cursor: default;
  }
`;
/** 모집이 다 찬 사연 — 대화하기 자리를 안내 라벨로 대체한다. */
const DoneButton = styled(ChatButton)`
  background: #f4f4f4;
  color: #b7b7b7;
  cursor: default;
  &:disabled {
    background: #f4f4f4;
    color: #b7b7b7;
  }
`;
const DetailButton = styled.button`
  height: 36px;
  border-radius: 18px;
  border: 1px solid #e6e6e6;
  background: #ffffff;
  color: #767676;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  &:hover {
    border-color: #ff6969;
    color: #ff4651;
  }
`;

export default DonationCard;
